import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "@/lib/api";

const AuthContext = createContext(null);

// One-time legacy cleanup: remove the old localStorage token (was used before
// we switched to HttpOnly session cookies). Safe no-op if it doesn't exist.
const purgeLegacyToken = () => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("w3rc_token");
    }
  } catch (err) {
    // localStorage may be unavailable (privacy mode, SSR, etc.); ignore.
    console.debug("auth: legacy token purge skipped", err);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    purgeLegacyToken();
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    setUser(res.user);
    return res.user;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authApi.register(data);
    if (res.access_token && res.user) {
      setUser(res.user);
    }
    return res;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Server-side logout best-effort; we still clear local state.
      console.debug("auth: server-side logout failed (state cleared locally)", err);
    }
    setUser(null);
  }, []);

  // Memoize the context value so consumers don't re-render every time
  // AuthProvider itself re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
